/**
 * Tools Common Utilities
 *
 * Shared helpers: step navigation, timer, breathing animation, AJAX, charts.
 */

export function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
}

/* ── Chart time label helper ─────────────────────────────── */

function chartTimeLabel(sec) {
    if (sec < 60) return sec + 's';
    var m = Math.floor(sec / 60), s = sec % 60;
    return s > 0 ? m + ':' + (s < 10 ? '0' : '') + s : m + 'm';
}

/* ── Fear-over-time SVG chart ───────────────────────────── */

var _cid = 0;

function smoothPath(pts) {
    if (pts.length < 2) return '';
    if (pts.length === 2)
        return 'M' + pts[0].x.toFixed(1) + ' ' + pts[0].y.toFixed(1) + 'L' + pts[1].x.toFixed(1) + ' ' + pts[1].y.toFixed(1);
    var d = 'M' + pts[0].x.toFixed(1) + ' ' + pts[0].y.toFixed(1);
    var t = 0.3;
    for (var i = 0; i < pts.length - 1; i++) {
        var p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
        d += 'C' + (p1.x + (p2.x - p0.x) * t).toFixed(1) + ' ' + (p1.y + (p2.y - p0.y) * t).toFixed(1)
            + ',' + (p2.x - (p3.x - p1.x) * t).toFixed(1) + ' ' + (p2.y - (p3.y - p1.y) * t).toFixed(1)
            + ',' + p2.x.toFixed(1) + ' ' + p2.y.toFixed(1);
    }
    return d;
}

export function renderFearChart(container, ratings, timestamps, totalDuration, opts) {
    if (!container) return;
    if (!ratings || ratings.length < 1) {
        container.innerHTML = '<p style="text-align:center;color:#999;font-size:13px;">No fear ratings recorded</p>';
        return;
    }
    _cid++;
    var gid = 'fcg' + _cid;
    var o = Object.assign({
        width: 480, height: 170,
        pL: 36, pR: 14, pT: 28, pB: 28,
        showLabels: true, showDots: true, showGrid: true, showValues: true,
        lineColor: '#FF6D8B', fillOpacity: 0.15, yLabel: 'Fear',
    }, opts || {});
    var pW = o.width - o.pL - o.pR, pH = o.height - o.pT - o.pB;
    var ts = (timestamps && timestamps.length === ratings.length) ? timestamps : ratings.map(function (_, i) { return (i + 1) * 30; });
    var maxT = Math.max(totalDuration, ts[ts.length - 1] || 0);
    var pts = ratings.map(function (v, i) {
        return { x: o.pL + (ts[i] / maxT) * pW, y: o.pT + (1 - v / 10) * pH, v: v, t: ts[i] };
    });
    var s = '<svg viewBox="0 0 ' + o.width + ' ' + o.height + '" xmlns="http://www.w3.org/2000/svg" class="tool-chart-svg">';
    s += '<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1">';
    s += '<stop offset="0%" stop-color="' + o.lineColor + '" stop-opacity="' + o.fillOpacity + '"/>';
    s += '<stop offset="100%" stop-color="' + o.lineColor + '" stop-opacity="0.01"/></linearGradient></defs>';

    var gridC = '#EBEBEB', textC = '#BBB';

    if (o.showGrid) { for (var g = 2; g <= 8; g += 2) { var gy = o.pT + (1 - g / 10) * pH; s += '<line x1="' + o.pL + '" y1="' + gy.toFixed(1) + '" x2="' + (o.width - o.pR) + '" y2="' + gy.toFixed(1) + '" stroke="' + gridC + '" stroke-width="1"/>'; } }
    if (o.showLabels) {
        [0, 5, 10].forEach(function (v) {
            var ly = o.pT + (1 - v / 10) * pH;
            s += '<text x="' + (o.pL - 6) + '" y="' + (ly + 4).toFixed(1) + '" text-anchor="end" font-size="10" fill="' + textC + '" font-family="system-ui,sans-serif">' + v + '</text>';
        });
        s += '<text x="' + o.pL + '" y="12" text-anchor="start" font-size="9" fill="' + textC + '" font-family="system-ui,sans-serif">' + o.yLabel + '</text>';
    }

    if (o.showLabels) {
        var step = maxT <= 60 ? 10 : maxT <= 180 ? 30 : 60;
        var by = o.height - 6;
        for (var xt = 0; xt <= maxT; xt += step) {
            var xx = o.pL + (xt / maxT) * pW;
            s += '<text x="' + xx.toFixed(1) + '" y="' + by + '" text-anchor="middle" font-size="10" fill="' + textC + '" font-family="system-ui,sans-serif">' + chartTimeLabel(xt) + '</text>';
        }
    }

    if (pts.length === 1) {
        s += '<circle cx="' + pts[0].x.toFixed(1) + '" cy="' + pts[0].y.toFixed(1) + '" r="5" fill="' + o.lineColor + '"/>';
    } else {
        var lp = smoothPath(pts);
        var bl = o.pT + pH;
        s += '<path d="' + lp + 'L' + pts[pts.length - 1].x.toFixed(1) + ',' + bl.toFixed(1) + 'L' + pts[0].x.toFixed(1) + ',' + bl.toFixed(1) + 'Z" fill="url(#' + gid + ')"/>';
        s += '<path d="' + lp + '" fill="none" stroke="' + o.lineColor + '" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>';
        if (o.showDots) pts.forEach(function (p) { s += '<circle cx="' + p.x.toFixed(1) + '" cy="' + p.y.toFixed(1) + '" r="3.5" fill="#fff" stroke="' + o.lineColor + '" stroke-width="2"/>'; });
    }
    if (o.showValues) pts.forEach(function (p) { s += '<text x="' + p.x.toFixed(1) + '" y="' + (p.y - 8).toFixed(1) + '" text-anchor="middle" font-size="10" font-weight="600" fill="' + o.lineColor + '" font-family="system-ui,sans-serif">' + p.v + '</text>'; });
    s += '</svg>';
    container.innerHTML = s;
}

/* ── Stress-over-cycles chart for Micro-Break ───────────── */

export function renderStressChart(container, sessionLog) {
    if (!container || !sessionLog || sessionLog.length < 1) return;

    var stressVals = [], labels = [], breaks = [];
    sessionLog.forEach(function (e) {
        if (e.stress === null || e.stress === undefined) return;
        stressVals.push(e.stress);
        labels.push('#' + e.cycle);
        breaks.push(!!e.break_taken);
    });
    if (stressVals.length < 1) {
        container.innerHTML = '<p style="text-align:center;color:#999;font-size:13px;">No stress data recorded</p>';
        return;
    }

    _cid++;
    var gid = 'scg' + _cid;
    var W = 480, H = 190, pL = 36, pR = 14, pT = 28, pB = 46;
    var pW = W - pL - pR, pH = H - pT - pB;
    var n = stressVals.length;
    var maxX = Math.max(n - 1, 1);
    var maxY = 5;

    var pts = stressVals.map(function (v, i) {
        return {
            x: pL + (i / maxX) * pW,
            y: pT + (1 - Math.min(Math.max(v, 1), maxY) / maxY) * pH,
            v: v
        };
    });

    var color = '#8B5CF6';
    var s = '<svg viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" class="tool-chart-svg">';
    s += '<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1">';
    s += '<stop offset="0%" stop-color="' + color + '" stop-opacity="0.15"/>';
    s += '<stop offset="100%" stop-color="' + color + '" stop-opacity="0.01"/></linearGradient></defs>';

    var gridC = '#EBEBEB', textC = '#BBB';

    [1, 3, 5].forEach(function (v) {
        var yy = pT + (1 - v / maxY) * pH;
        s += '<line x1="' + pL + '" y1="' + yy.toFixed(1) + '" x2="' + (W - pR) + '" y2="' + yy.toFixed(1) + '" stroke="' + gridC + '" stroke-width="1"/>';
        s += '<text x="' + (pL - 6) + '" y="' + (yy + 4).toFixed(1) + '" text-anchor="end" font-size="10" fill="' + textC + '" font-family="system-ui,sans-serif">' + v + '</text>';
    });

    s += '<text x="' + pL + '" y="12" text-anchor="start" font-size="9" fill="' + textC + '" font-family="system-ui,sans-serif">Stress</text>';

    var xLabelY = pT + pH + 16;
    var dotY = xLabelY + 14;
    for (var i = 0; i < n; i++) {
        var xx = pts[i].x;
        s += '<text x="' + xx.toFixed(1) + '" y="' + xLabelY + '" text-anchor="middle" font-size="10" fill="' + textC + '" font-family="system-ui,sans-serif">' + labels[i] + '</text>';
        var bClr = breaks[i] ? '#29CC6A' : '#E0E0E0';
        s += '<circle cx="' + xx.toFixed(1) + '" cy="' + dotY + '" r="3.5" fill="' + bClr + '"/>';
    }

    s += '<text x="' + (W - pR) + '" y="' + (H - 3) + '" text-anchor="end" font-size="9" fill="' + textC + '" font-family="system-ui,sans-serif">';
    s += '<tspan fill="#29CC6A">\u25CF</tspan> break  <tspan fill="#E0E0E0">\u25CF</tspan> skipped</text>';

    if (pts.length === 1) {
        s += '<circle cx="' + pts[0].x.toFixed(1) + '" cy="' + pts[0].y.toFixed(1) + '" r="5" fill="' + color + '"/>';
    } else {
        var lp = smoothPath(pts);
        var bl = pT + pH;
        s += '<path d="' + lp + 'L' + pts[pts.length - 1].x.toFixed(1) + ',' + bl.toFixed(1) + 'L' + pts[0].x.toFixed(1) + ',' + bl.toFixed(1) + 'Z" fill="url(#' + gid + ')"/>';
        s += '<path d="' + lp + '" fill="none" stroke="' + color + '" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>';
        pts.forEach(function (p) {
            s += '<circle cx="' + p.x.toFixed(1) + '" cy="' + p.y.toFixed(1) + '" r="3.5" fill="#fff" stroke="' + color + '" stroke-width="2"/>';
        });
    }

    pts.forEach(function (p) {
        s += '<text x="' + p.x.toFixed(1) + '" y="' + (p.y - 8).toFixed(1) + '" text-anchor="middle" font-size="10" font-weight="600" fill="' + color + '" font-family="system-ui,sans-serif">' + p.v + '</text>';
    });

    s += '</svg>';
    container.innerHTML = s;
}

/* ── Timer completion chime ──────────────────────────────── */

export class TimerChime {
    constructor() {
        this.ctx = null;
        this.enabled = true;
    }

    _ensureCtx() {
        if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (this.ctx.state === 'suspended') this.ctx.resume();
    }

    play() {
        if (!this.enabled) return;
        this._ensureCtx();
        var ctx = this.ctx, now = ctx.currentTime;
        var notes = [523, 659, 784];
        for (var i = 0; i < notes.length; i++) {
            var t = now + i * 0.15;
            var osc = ctx.createOscillator();
            var gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = notes[i];
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(0.12, t + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(t);
            osc.stop(t + 0.45);
        }
    }
}

/* ── Load past tool entries via AJAX ────────────────────── */

export function loadToolEntries(toolSlug, limit) {
    if (typeof pe_mp_tools === 'undefined' || !pe_mp_tools.is_logged_in) return Promise.resolve([]);
    var fd = new FormData();
    fd.append('action', 'pe_tools_get_entries');
    fd.append('nonce', pe_mp_tools.nonce);
    fd.append('tool_slug', toolSlug || '');
    fd.append('limit', limit || 10);
    return fetch(pe_mp_tools.ajax_url, { method: 'POST', credentials: 'same-origin', body: fd })
        .then(function (r) { return r.json(); })
        .then(function (resp) { return (resp.success && resp.data && resp.data.entries) ? resp.data.entries : []; })
        .catch(function () { return []; });
}

/**
 * Step-based navigation for tool flows.
 */
export class StepFlow {
    constructor(rootEl) {
        this.root = rootEl;
        this.steps = rootEl.querySelectorAll('.tool-step');
    }

    show(stepName) {
        this.steps.forEach(function (el) {
            if (el.dataset.step === stepName) {
                el.classList.add('tool-step--active');
            } else {
                el.classList.remove('tool-step--active');
            }
        });
    }

    reset() {
        this.steps.forEach(function (el, i) {
            if (i === 0) {
                el.classList.add('tool-step--active');
            } else {
                el.classList.remove('tool-step--active');
            }
        });
    }
}

/**
 * Countdown timer with callbacks.
 */
export class CountdownTimer {
    constructor(totalSeconds, onTick, onComplete) {
        this.total = totalSeconds;
        this.remaining = totalSeconds;
        this.onTick = onTick;
        this.onComplete = onComplete;
        this.interval = null;
        this.paused = false;
    }

    start() {
        this.paused = false;
        this.interval = setInterval(() => {
            if (this.paused) return;
            this.remaining--;
            if (this.onTick) this.onTick(this.remaining, this.total);
            if (this.remaining <= 0) {
                this.stop();
                if (this.onComplete) this.onComplete();
            }
        }, 1000);
        if (this.onTick) this.onTick(this.remaining, this.total);
    }

    pause() {
        this.paused = true;
    }

    resume() {
        this.paused = false;
    }

    stop() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }

    reset(totalSeconds) {
        this.stop();
        this.total = totalSeconds || this.total;
        this.remaining = this.total;
    }
}

/**
 * Breathing guide sounds using Web Audio API.
 * Soft rhythmic clicks that span the full phase duration.
 * Pitch rises on inhale, holds steady, falls on exhale.
 */
export class BreathingSounds {
    constructor() {
        this.ctx = null;
        this.enabled = false;
        this.volume = 0.07;
        this._scheduled = [];
    }

    enable() {
        if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (this.ctx.state === 'suspended') this.ctx.resume();
        this.enabled = true;
    }

    disable() {
        this.enabled = false;
        this._stopAll();
    }

    toggle() {
        if (this.enabled) this.disable(); else this.enable();
        return this.enabled;
    }

    destroy() {
        this.disable();
        if (this.ctx) { this.ctx.close(); this.ctx = null; }
    }

    play(phase, duration) {
        if (!this.enabled || !this.ctx) return;
        if (this.ctx.state === 'suspended') this.ctx.resume();
        this._stopAll();
        var d = duration || 4;
        switch (phase) {
            case 'inhale': this._taps(280, 560, d, Math.round(d * 4)); break;
            case 'hold':   this._taps(420, 420, d, Math.round(d * 3)); break;
            case 'exhale':  this._taps(560, 280, d, Math.round(d * 3.5)); break;
        }
    }

    _stopAll() {
        this._scheduled.forEach(function (n) { try { n.stop(); } catch (e) {} });
        this._scheduled = [];
    }

    _taps(f0, f1, dur, count) {
        if (count < 1) count = 1;
        var ctx = this.ctx, now = ctx.currentTime;
        var gap = dur / (count + 1);
        for (var i = 0; i < count; i++) {
            var t = now + (i + 1) * gap;
            var p = count > 1 ? i / (count - 1) : 0;
            this._tap(t, f0 + (f1 - f0) * p);
        }
    }

    _tap(time, freq) {
        var ctx = this.ctx;
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq * 1.15, time);
        osc.frequency.exponentialRampToValueAtTime(Math.max(freq, 20), time + 0.012);
        var d = 0.06;
        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(this.volume, time + 0.002);
        gain.gain.exponentialRampToValueAtTime(0.001, time + d);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(time);
        osc.stop(time + d + 0.005);
        this._scheduled.push(osc);
    }
}

/**
 * Configurable breathing animation controller.
 * Pattern: inhale -> hold (optional) -> exhale -> repeat
 * Default: inhale 4s, hold 2s, exhale 6s (4:6 with hold).
 * Options: { inhale, hold, exhale } in seconds; hold can be 0.
 */
export class BreathingAnimation {
    constructor(circleEl, instructionEl, counterEl, sounds, pattern) {
        this.circle = circleEl;
        this.instruction = instructionEl;
        this.counter = counterEl;
        this.sounds = sounds || null;
        this.pattern = pattern && typeof pattern === 'object'
            ? { inhale: pattern.inhale || 4, hold: pattern.hold != null ? pattern.hold : 2, exhale: pattern.exhale || 6 }
            : { inhale: 4, hold: 2, exhale: 6 };
        this.running = false;
        this.timeouts = [];
        this.onCycleComplete = null;
    }

    start() {
        this.running = true;
        this._cycle();
    }

    stop() {
        this.running = false;
        this.timeouts.forEach(clearTimeout);
        this.timeouts = [];
        if (this.circle) {
            this.circle.classList.remove('breathing-circle--inhale', 'breathing-circle--exhale', 'breathing-circle--hold');
        }
    }

    _cycle() {
        if (!this.running) return;
        var self = this;
        var inhale = this.pattern.inhale;
        var hold = this.pattern.hold;
        var exhale = this.pattern.exhale;

        this._phase('inhale', 'Breathe in', inhale, function () {
            if (hold > 0) {
                self._phase('hold', 'Hold', hold, function () {
                    self._phase('exhale', 'Breathe out', exhale, function () {
                        if (self.onCycleComplete) self.onCycleComplete();
                        self._cycle();
                    });
                });
            } else {
                self._phase('exhale', 'Breathe out', exhale, function () {
                    if (self.onCycleComplete) self.onCycleComplete();
                    self._cycle();
                });
            }
        });
    }

    _phase(phaseName, label, duration, callback) {
        if (!this.running) return;

        if (this.circle) {
            this.circle.classList.remove('breathing-circle--inhale', 'breathing-circle--exhale', 'breathing-circle--hold');
            this.circle.classList.add('breathing-circle--' + phaseName);
        }
        if (this.instruction) this.instruction.textContent = label;
        if (this.sounds) this.sounds.play(phaseName, duration);

        let count = duration;
        if (this.counter) this.counter.textContent = count;

        const tick = () => {
            if (!this.running) return;
            count--;
            if (count > 0) {
                if (this.counter) this.counter.textContent = count;
                this.timeouts.push(setTimeout(tick, 1000));
            } else {
                if (callback) callback();
            }
        };
        this.timeouts.push(setTimeout(tick, 1000));
    }
}

/**
 * Save tool entry via AJAX.
 */
export function saveToolEntry(data) {
    if (typeof pe_mp_tools === 'undefined' || !pe_mp_tools.is_logged_in) {
        return Promise.resolve(null);
    }

    var formData = new FormData();
    formData.append('action', 'pe_tools_save_entry');
    formData.append('nonce', pe_mp_tools.nonce);
    formData.append('tool_slug', data.tool_slug || '');
    formData.append('entry_type', data.entry_type || 'session');
    formData.append('data', JSON.stringify(data.data || {}));

    if (data.anxiety_before !== undefined && data.anxiety_before !== null) {
        formData.append('anxiety_before', data.anxiety_before);
    }
    if (data.anxiety_after !== undefined && data.anxiety_after !== null) {
        formData.append('anxiety_after', data.anxiety_after);
    }

    return fetch(pe_mp_tools.ajax_url, {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
    })
    .then(function (r) { return r.json(); })
    .catch(function () { return null; });
}

/**
 * Wire up button-group selection (single-select toggle).
 */
export function initButtonGroup(container, btnSelector, callback) {
    var buttons = container.querySelectorAll(btnSelector);
    buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            buttons.forEach(function (b) { b.classList.remove('active'); });
            btn.classList.add('active');
            if (callback) callback(btn);
        });
    });
}
